import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  clearTenantSession,
  getTenantStoredUser,
  saveTenantSession,
} from '@saasplatform/auth';
import { canAccessTenantPath, getTenantLandingPath } from '@saasplatform/auth';
import { getTenantCurrentUser } from '../api/tenantAuth';

const TENANT_CURRENT_USER_SYNC_AT_KEY = 'feedsaas_tenant_current_user_sync_at';
const TENANT_CURRENT_USER_SYNC_TTL_MS = 15 * 1000;

const authStatusStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f5f7fb',
  color: '#64748b',
};

const AuthStatus: React.FC<{ message: string }> = ({ message }) => (
  <div style={authStatusStyle}>{message}</div>
);

const readTenantCurrentUserSyncAt = () => {
  if (typeof window === 'undefined') {
    return 0;
  }
  try {
    return Number(window.sessionStorage.getItem(TENANT_CURRENT_USER_SYNC_AT_KEY) || 0);
  } catch {
    return 0;
  }
};

export const markTenantCurrentUserSynced = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      TENANT_CURRENT_USER_SYNC_AT_KEY,
      String(Date.now()),
    );
  } catch {
    // ignore sync marker write failures
  }
};

const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { initialized, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const isTenantRoute = /^\/tenant(\/|$)/.test(location.pathname);
  const hasTempToken = new URLSearchParams(location.search).has('temp_token');
  const [tenantUser, setTenantUser] = useState(() => getTenantStoredUser());
  const [tenantReady, setTenantReady] = useState(!isTenantRoute);

  useEffect(() => {
    if (!isTenantRoute) {
      setTenantReady(true);
      return;
    }

    const storedTenantUser = getTenantStoredUser();
    setTenantUser(storedTenantUser);

    let active = true;
    if (!storedTenantUser) {
      setTenantReady(false);
    } else {
      setTenantReady(true);
    }

    const lastSyncedAt = readTenantCurrentUserSyncAt();
    if (
      storedTenantUser &&
      Date.now() - lastSyncedAt < TENANT_CURRENT_USER_SYNC_TTL_MS
    ) {
      return () => {
        active = false;
      };
    }

    getTenantCurrentUser()
      .then((user) => {
        if (!active) {
          return;
        }
        const normalizedUser = {
          ...user,
          id: String(user.id),
          tenantId: String(user.tenantId),
        };
        saveTenantSession(null, normalizedUser);
        markTenantCurrentUserSynced();
        setTenantUser(normalizedUser);
        setTenantReady(true);
      })
      .catch(() => {
        if (!active) {
          return;
        }
        if (!storedTenantUser) {
          clearTenantSession();
          setTenantUser(null);
        } else {
          setTenantUser(storedTenantUser);
        }
        setTenantReady(true);
      });

    return () => {
      active = false;
    };
  }, [isTenantRoute]);

  // 兼容旧的一键登录链接：先放行带 temp_token 的受保护页，再由页面立即跳到公共中转页
  if (hasTempToken) {
    return <>{children}</>;
  }

  // 租户路由：检查 localStorage 里的 tenant access_token
  if (isTenantRoute) {
    if (!tenantReady) {
      return <AuthStatus message="正在恢复租户登录状态..." />;
    }
    if (!tenantUser) {
      return <Navigate to="/tenant/login" state={{ from: location }} replace />;
    }
    if (tenantUser?.mustChangePassword && location.pathname !== '/tenant/profile') {
      return <Navigate to="/tenant/profile" state={{ from: location, forcePasswordChange: true }} replace />;
    }
    if (tenantUser && !canAccessTenantPath(tenantUser, location.pathname)) {
      return <Navigate to={getTenantLandingPath(tenantUser)} replace />;
    }
    return <>{children}</>;
  }

  // 管理后台路由：未登录则跳转到管理后台登录页
  if (!initialized) {
    return <AuthStatus message="正在恢复登录状态..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default AuthRoute;
